import { getAlgoliaProductIndex } from "@utils/configuration/aem";

import { AlgoliaTrackingService } from "@services/algolia/tracking/algoliaTracking.service";
import { TypedAnalyticsEvent } from "@services/tracking/types/analyticsEvent";
import { CartProductTrackingInfo } from "@services/tracking/types/trackingInfo/cartProductTrackingInfo";
import { CheckoutToolStepTrackingInfo } from "@services/tracking/types/trackingInfo/toolStepTrackingInfo";

import {
    AlgoliaAnalyticsEventNames,
    AlgoliaAnalyticsEvents,
} from "../AlgoliaAnalyticsEvents";
import {
    AlgoliaAnalyticsMinimumDataset,
    AlgoliaObjectData,
    AlgoliaPurchasePayload,
} from "../AlgoliaAnalyticsMinimumDataset";

export interface AlgoliaAnalyticsPurchaseImpression
    extends AlgoliaAnalyticsMinimumDataset<AlgoliaPurchasePayload> {}

const mapEventDataToAlgoliaObjectData = (
    cartItems: Array<CartProductTrackingInfo>,
): Array<AlgoliaObjectData & { sku: string }> => {
    return cartItems.map(ci => ({
        price: ci.unitPrice,
        quantity: ci.quantity,
        queryID: AlgoliaTrackingService.getLastQueryTrackRecordFor(ci.id)
            ?.queryID,
        sku: ci.id,
    }));
};

const getAlgoliaPurchasePayloads = (
    eventData: CheckoutToolStepTrackingInfo,
): Array<AlgoliaAnalyticsPurchaseImpression> => {
    const objectData = mapEventDataToAlgoliaObjectData(eventData.cart.items);

    const objectsWithQueryId = objectData.filter(d => !!d.queryID);
    const objectsWithoutQueryId = objectData.filter(d => !d.queryID);

    const payloads: Array<AlgoliaAnalyticsPurchaseImpression> = [];

    if (objectsWithQueryId.length > 0) {
        // afterSearch
        payloads.push({
            eventName: AlgoliaAnalyticsEvents.PurchaseAfterSearch,
            payload: {
                authenticatedUserToken:
                    AlgoliaTrackingService.getAuthenticatedUserToken(),
                currency: "EUR",
                eventName: AlgoliaAnalyticsEventNames.PurchaseAfterSearch,
                index: getAlgoliaProductIndex(),
                userToken: AlgoliaTrackingService.getUserToken(),
                objectIDs: objectsWithQueryId.map(o => o.sku),
                objectData: objectsWithQueryId.map(o => {
                    const { sku, ...data } = o;
                    return data;
                }),
            },
        });
    }

    if (objectsWithoutQueryId.length > 0) {
        // nosearch
        payloads.push({
            eventName: AlgoliaAnalyticsEvents.PurchaseNoSearch,
            payload: {
                authenticatedUserToken:
                    AlgoliaTrackingService.getAuthenticatedUserToken(),
                currency: "EUR",
                eventName: AlgoliaAnalyticsEventNames.Purchase,
                index: getAlgoliaProductIndex(),
                userToken: AlgoliaTrackingService.getUserToken(),
                objectIDs: objectsWithoutQueryId.map(o => o.sku),
                objectData: objectsWithoutQueryId.map(o => {
                    const { sku, ...data } = o;
                    return data;
                }),
            },
        });
    }

    return payloads;
};

const getAlgoliaAnalyticsPurchaseImpression = (
    data: TypedAnalyticsEvent<CheckoutToolStepTrackingInfo>,
): Array<AlgoliaAnalyticsPurchaseImpression> => {
    const payloads = getAlgoliaPurchasePayloads(data.payload.eventData);

    data.payload.eventData.cart.items
        .map(i => i.id)
        .forEach(AlgoliaTrackingService.removeQueryTrackRecordFor);

    return payloads;
};

export { getAlgoliaAnalyticsPurchaseImpression };
